var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { useGesture } from '@use-gesture/react';
import { useMemo, useCallback, useEffect, useRef, useState, memo, Children, } from 'react';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { CarouselButton } from './CarouselButton';
import { CarouselDot } from './CarouselDot';
import { slideIndicatorPlacementOptions, carouselButtonNavigationTypes, } from './constants';
const scrollDirections = {
    backward: 'backward',
    forward: 'forward',
    none: 'none',
};
const defaultProps = {
    afterSlide: () => { },
    autoplay: false,
    autoplaySpeed: 2500,
    fullWidth: false,
    fullWidthAt: 'small',
    initialActiveSlide: 0,
    slideIndicatorPlacement: slideIndicatorPlacementOptions.none,
    slideWidth: '100%',
    transitionDuration: '500ms',
    wrap: true,
};
const Carousel = (_a) => {
    var { initialActiveSlide = defaultProps.initialActiveSlide, afterSlide = defaultProps.afterSlide, autoplay = defaultProps.autoplay, autoplaySpeed = defaultProps.autoplaySpeed, children, fullWidth = defaultProps.fullWidth, fullWidthAt = defaultProps.fullWidthAt, slideIndicatorPlacement = defaultProps.slideIndicatorPlacement, slideWidth = defaultProps.slideWidth, transitionDuration = defaultProps.transitionDuration, wrap = defaultProps.wrap } = _a, props = __rest(_a, ["initialActiveSlide", "afterSlide", "autoplay", "autoplaySpeed", "children", "fullWidth", "fullWidthAt", "slideIndicatorPlacement", "slideWidth", "transitionDuration", "wrap"]);
    const { 
    /* eslint-disable-next-line deprecation/deprecation */
    activeSlide: deprecatedActiveSlide, } = props;
    const initialSlide = useMemo(() => (deprecatedActiveSlide || initialActiveSlide), [
        deprecatedActiveSlide,
        initialActiveSlide,
    ]);
    const [autoplayInterval, setAutoplayInterval,] = useState(Infinity);
    const carouselRef = useRef(null);
    const slideRef = useRef(null);
    const [slideCount, setSlideCount,] = useState(0);
    const showCarouselDots = useMemo(() => (slideIndicatorPlacement !== slideIndicatorPlacementOptions.none), [
        slideIndicatorPlacement,
    ]);
    const [activeSlide, setActiveSlide,] = useState(initialSlide);
    const [nextActiveSlide, setNextActiveSlide,] = useState(initialSlide);
    const [scrollDirection, setScrollDirection,] = useState('none');
    const [isScrollable, setIsScrollable,] = useState(false);
    const dots = useMemo(() => (showCarouselDots
        && (Array(slideCount)
            .fill(null)
            .map((item, index) => (_jsx(CarouselDot, { ariaLabel: `Go to slide ${index + 1}`, isSelected: activeSlide === index, modifier: slideIndicatorPlacement === slideIndicatorPlacementOptions.inside ? 'light' : 'dark', onClick: (event) => {
                event.stopPropagation();
                setNextActiveSlide(index);
                setActiveSlide(index);
            } }, `slideDot-${index + 1}`))))), [
        activeSlide,
        showCarouselDots,
        slideCount,
        slideIndicatorPlacement,
    ]);
    const onNextSlide = useCallback(() => {
        if (wrap === false
            && activeSlide === (slideCount - 1)) {
            return;
        }
        setScrollDirection('forward');
        setNextActiveSlide((slide) => ((slide + 1) % slideCount));
    }, [
        activeSlide,
        slideCount,
        wrap,
    ]);
    const onPreviousSlide = useCallback(() => {
        const nextTarget = ((activeSlide - 1) < 0
            ? slideCount - 1
            : activeSlide - 1);
        setScrollDirection('backward');
        setNextActiveSlide(nextTarget % slideCount);
    }, [
        activeSlide,
        slideCount,
    ]);
    const getSlideOrder = useCallback((slideIndex) => {
        // If too few items to scroll don't reorder slides.
        if (!isScrollable) {
            return null;
        }
        let refSlide = activeSlide - 1;
        if (refSlide < 0) {
            refSlide = slideCount + refSlide;
        }
        let order = slideIndex - refSlide;
        if (order < 0) {
            order = slideCount + order;
        }
        return order;
    }, [
        activeSlide,
        isScrollable,
        slideCount,
    ]);
    const onSlideListTransitionEnd = useCallback(() => {
        setActiveSlide(nextActiveSlide);
        setScrollDirection('none');
        afterSlide({
            isReversing: false,
            istransitioning: false,
            nextActive: nextActiveSlide,
            nextActiveSlide,
            scrollDirection,
        });
    }, [
        afterSlide,
        nextActiveSlide,
        scrollDirection,
    ]);
    const onSlideTransitionEnd = useCallback((event) => {
        event.stopPropagation();
    }, []);
    const onAutoplay = useCallback(() => {
        setAutoplayInterval((autoplayIntervalId) => {
            clearInterval(autoplayIntervalId);
            return (setInterval(onNextSlide, autoplaySpeed));
        });
    }, [
        autoplaySpeed,
        onNextSlide,
    ]);
    const onMouseEnter = useCallback(() => {
        if (autoplay && autoplayInterval) {
            clearInterval(autoplayInterval);
        }
    }, [
        autoplayInterval,
        autoplay,
    ]);
    const onMouseLeave = useCallback(() => {
        if (autoplay) {
            onAutoplay();
        }
    }, [
        autoplay,
        onAutoplay,
    ]);
    const carouselButtonSpacing = useMemo(() => ((fullWidth
        || !isScrollable)
        ? '0'
        : theme.spacing.x8), [
        fullWidth,
        isScrollable,
    ]);
    const slideOffset = useMemo(() => (fullWidth
        ? '100%'
        : slideWidth), [
        fullWidth,
        slideWidth,
    ]);
    const transitionStyles = useCallback((offset) => (css `
          left: -${offset};
          ${scrollDirection === 'forward' && css `
            transform: translate3D(-${offset}, 0, 0);
          `}
          ${scrollDirection === 'backward' && css `
            transform: translate3D(${offset}, 0, 0);
          `}
          ${scrollDirection !== 'none' && css `
            transition: transform ${transitionDuration} ease-out;
          `}
        `), [
        scrollDirection,
        transitionDuration,
    ]);
    const isPreviousButtonVisible = useMemo(() => (isScrollable
        && (wrap
            || (activeSlide !== 0))), [
        activeSlide,
        isScrollable,
        wrap,
    ]);
    const isNextButtonVisible = useMemo(() => (isScrollable
        && (wrap
            || (activeSlide !== slideCount - 1))), [
        activeSlide,
        isScrollable,
        slideCount,
        wrap,
    ]);
    const containerStyles = useMemo(() => (css `
        ${styleUtils.borderBox}
        position: relative;
        width: 100%;

        ${!fullWidth && css `
          padding-left: ${theme.spacing.x2};
          padding-right: ${theme.spacing.x2};
        `}

        ${fullWidthAt && theme.media.lessThan(fullWidthAt)(css `
          padding-left: 0;
          padding-right: 0;
        `)}
      `), [
        fullWidth,
        fullWidthAt,
    ]);
    const carouselContainerStyles = useMemo(() => (css `
        margin-bottom: 0;
        margin-left: ${carouselButtonSpacing};
        margin-top: 0;
        margin-right: ${carouselButtonSpacing};
        overflow: hidden;
        position: relative;
        ${fullWidthAt && theme.media.lessThan(fullWidthAt)(css `
          margin-bottom: 0;
          margin-left: 0;
          margin-top: 0;
          margin-right: 0;
        `)}
      `), [
        fullWidthAt,
        carouselButtonSpacing,
    ]);
    const slideListStyles = useMemo(() => (css `
        ${styleUtils.listReset}
        display: flex;
        position: relative;
        touch-action: none;
        ${!isScrollable && css `
          justify-content: center;
        `}
        ${isScrollable && transitionStyles(slideOffset)}
        ${fullWidthAt && (slideCount > 1) && theme.media.lessThan(fullWidthAt)(css `
          ${transitionStyles('100%')}
        `)}

        ${!fullWidth && css `
          column-gap: ${theme.spacing.x2};
        `}
      `), [
        fullWidth,
        fullWidthAt,
        isScrollable,
        transitionStyles,
        slideCount,
        slideOffset,
    ]);
    const dotStyles = useMemo(() => (css `
        display: flex;
        justify-content: center;
        margin-bottom: ${theme.spacing.x3};
        margin-left: auto;
        margin-right: auto;
        margin-top: ${theme.spacing.x3};

        ${slideIndicatorPlacement === slideIndicatorPlacementOptions.inside && css `
          margin-bottom: -${theme.spacing.x6};
          margin-left: auto;
          margin-right: auto;
          margin-top: -${theme.spacing.x6};
          position: absolute;
          width: 100%;
          z-index: 1;
        `}
      `), [
        slideIndicatorPlacement,
    ]);
    const getGestureProps = useGesture({
        onDragEnd: (state) => {
            const [xAxisValue,] = state.direction;
            if (xAxisValue === 0) {
                state.event.stopPropagation();
            }
            else if (xAxisValue > 0) {
                onPreviousSlide();
            }
            else if (xAxisValue < 0) {
                onNextSlide();
            }
            onMouseLeave();
        },
        onDragStart: () => (onMouseEnter),
    }, {});
    useEffect(() => {
        setSlideCount(Children.count(children));
    }, [
        children,
    ]);
    useEffect(() => {
        if (autoplay) {
            onAutoplay();
        }
    }, [
        autoplay,
        onAutoplay,
    ]);
    const onUpdate = useCallback(() => {
        var _a, _b;
        const slideCount = Children.count(children);
        const totalGapWidth = styleUtils.remToPxValue(theme.spacing.x8) + (slideCount - 1);
        const totalSlideWidth = (((_a = slideRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0) * slideCount;
        const isOverflowing = (totalSlideWidth + totalGapWidth) > (((_b = carouselRef.current) === null || _b === void 0 ? void 0 : _b.offsetWidth) || 0);
        setIsScrollable(isOverflowing);
    }, [
        children,
    ]);
    useEffect(() => {
        onUpdate();
    }, [
        onUpdate,
    ]);
    useEffect(() => {
        window.addEventListener('resize', onUpdate);
        return () => {
            window.removeEventListener('resize', onUpdate);
        };
    }, [
        onUpdate,
    ]);
    return (_jsxs("div", Object.assign({ css: containerStyles, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, ref: carouselRef }, { children: [isPreviousButtonVisible
                && (_jsx(CarouselButton, { direction: carouselButtonNavigationTypes.previous, fullWidth: fullWidth, fullWidthAt: fullWidthAt, onClick: onPreviousSlide })), isNextButtonVisible
                && (_jsx(CarouselButton, { direction: carouselButtonNavigationTypes.next, fullWidth: fullWidth, fullWidthAt: fullWidthAt, onClick: onNextSlide })), _jsx("div", Object.assign({ css: carouselContainerStyles }, { children: _jsx("ul", Object.assign({}, getGestureProps(), { css: slideListStyles, onTransitionEnd: onSlideListTransitionEnd }, { children: Children.map(children, (child, index) => {
                        const key = `slide-${index + 1}`;
                        return (_jsx("li", Object.assign({ css: css `
                        flex: 0 0 ${fullWidth ? '100%' : slideWidth};
                        order: ${getSlideOrder(index)};
                        ${fullWidthAt && theme.media.lessThan(fullWidthAt)(css `
                            flex-basis: 100%;
                            margin-bottom: 0;
                            margin-left: 0;
                            margin-right: 0;
                            margin-top: 0;
                        `)}
                      `, onTransitionEnd: onSlideTransitionEnd, ref: index === 0 ? slideRef : undefined }, { children: child }), key));
                    }) })) })), showCarouselDots
                && (_jsx("div", Object.assign({ css: dotStyles }, { children: dots })))] })));
};
const MemoizedCarousel = memo(Carousel);
export { MemoizedCarousel as Carousel };
// MC-1068 Temporary hack for Storybook.
export default Carousel;
